import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ExternalLink, ArrowLeft, ChevronRight, Instagram, Phone, Globe, Sparkles } from 'lucide-react';
import SEO from './SEO';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { SALON_SPA_STORES } from './SalonSpa';
import SocialLinks, { countSocialLinks } from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-21/4496207897c7.jpeg';

// Same key function SalonSpa.tsx uses for its managed collection, so the
// slug in the URL always matches the entry the grid card linked from.
function salonSlug(s: any, i: number) {
  return slugify(s.id || s.name || String(i));
}

function resolveMapsUrl(s: any) {
  const address = (s.address || '').trim();
  if (address && !address.startsWith('@') && /\d/.test(address)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${s.name}, ${address}`)}`;
  }
  return '';
}

export default function SalonSpaDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('salon_spa', SALON_SPA_STORES, (s, i) =>
    salonSlug(s, i)
  );

  const entry = mc.items.find((s: any, i: number) => salonSlug(s, i) === slug);

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Salon Not Found | Dallas Desi Scoop"
          description="This salon or spa listing could not be found."
          canonical="/salon-spa"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Salon or spa not found.</p>
        <Link
          to="/salon-spa"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Salon &amp; Spa
        </Link>
      </div>
    );
  }

  const typeLabel = (entry.description || '').trim() || 'Salon & Spa';
  const rawLocation = (entry.location || '').trim();
  const cityLabel = rawLocation
    ? rawLocation.includes('TX')
      ? rawLocation
      : `${rawLocation}, TX`
    : 'Dallas-Fort Worth';

  const resolvedMapsUrl = resolveMapsUrl(entry);
  const hasAnySocial = countSocialLinks(entry) > 0;
  const canonicalPath = `/salon-spa/${slug}`;

  const title = `${entry.name} | ${typeLabel} in ${cityLabel} | Dallas Desi Scoop`;
  const metaDescription = `${entry.name} is a ${typeLabel.toLowerCase()} in ${cityLabel} serving the South Asian community across the Dallas-Fort Worth metroplex. Find address, contact details, and more on Dallas Desi Scoop.`;

  const addressParts = (entry.address || '').split(',').map((p: string) => p.trim()).filter(Boolean);
  const streetAddress =
    addressParts.length >= 3 ? addressParts.slice(0, -2).join(', ') : undefined;
  const addressLocality =
    addressParts.length >= 2 ? addressParts[addressParts.length - 2] : undefined;

  const jsonLd = [
    {
      '@type': 'HealthAndBeautyBusiness',
      name: entry.name,
      image: entry.image || FALLBACK_IMAGE,
      description: `${entry.name}, ${typeLabel} in ${cityLabel}, serving the Dallas-Fort Worth Desi community.`,
      address:
        addressParts.length >= 2
          ? {
              '@type': 'PostalAddress',
              streetAddress,
              addressLocality: addressLocality || undefined,
              addressRegion: 'TX',
              addressCountry: 'US',
            }
          : undefined,
      telephone: entry.phone || undefined,
      url:
        entry.shopUrl ||
        entry.website ||
        entry.instagram ||
        `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Salon & Spa', item: `${BASE_URL}/salon-spa` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  // Internal linking: other salons & spas in the same city first.
  const relatedSalons = mc.items
    .map((s: any, i: number) => ({ store: s, key: salonSlug(s, i) }))
    .filter((r: any) => r.key !== slug)
    .sort(
      (a: any, b: any) =>
        Number((b.store.location || '') === (entry.location || '')) -
        Number((a.store.location || '') === (entry.location || ''))
    )
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={metaDescription}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/salon-spa"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Salon &amp; Spa
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{entry.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            <span className="flex items-center gap-1 text-white/85 text-[13px]">
              <Sparkles className="w-3.5 h-3.5 text-[#51ba4f]" />
              {typeLabel}
            </span>
            {entry.address && !entry.address.trim().startsWith('@') && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {entry.address}
              </span>
            )}
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {entry.name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {entry.name} is a {typeLabel.toLowerCase()} serving the South Asian community in {cityLabel} and across the Dallas-Fort Worth metroplex.
            {entry.address && !entry.address.trim().startsWith('@')
              ? ` You can visit them at ${entry.address}.`
              : ''}
            {` Contact ${entry.name} directly for appointments, pricing, and the full range of beauty and wellness services they offer.`}
          </p>

          {hasAnySocial && (
            <div className="mt-6">
              <SocialLinks item={entry} px={20} />
            </div>
          )}
        </div>

        {/* Contact & Info */}
        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact &amp; Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            {entry.address && !entry.address.trim().startsWith('@') && (
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{entry.address}</span>
              </div>
            )}
            {entry.phone && (
              <a
                href={`tel:${String(entry.phone).replace(/[^\d+]/g, '')}`}
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{entry.phone}</span>
              </a>
            )}
            {entry.website && (
              <a
                href={entry.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <Globe className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>Visit website</span>
              </a>
            )}
            {entry.shopUrl && (
              <a
                href={entry.shopUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                {entry.shopUrl.includes('facebook.com') ? (
                  <Globe className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                ) : (
                  <Instagram className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                )}
                <span>Follow {entry.name}</span>
              </a>
            )}
          </div>
          {!!resolvedMapsUrl && (
            <a
              href={resolvedMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Get Directions
            </a>
          )}
        </div>
      </div>

      {/* Internal links: related salons */}
      {relatedSalons.length > 0 && (
        <div className="max-w-[1000px] mx-auto px-4 pb-8">
          <h2 className="text-[16px] font-bold text-gray-900 mb-3">More Salons &amp; Spas in DFW</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {relatedSalons.map(({ store, key }: any) => (
              <Link
                key={key}
                to={`/salon-spa/${key}`}
                className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-4 py-3 text-[13px] font-medium text-gray-700 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5 text-[#f97316] shrink-0" />
                {store.name}
                <span className="ml-auto text-gray-400 text-[11px]">
                  {store.location || 'DFW'}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Internal links: explore DallasDesiScoop */}
      <div className="max-w-[1000px] mx-auto px-4 pb-12">
        <h2 className="text-[16px] font-bold text-gray-900 mb-3">Explore Dallas Desi Scoop</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { to: '/salon-spa', label: 'All Salons & Spas in DFW' },
            { to: '/BeautyServices', label: 'Beauty Services' },
            { to: '/restaurants', label: 'Desi Restaurants' },
            { to: '/events', label: 'Desi Events in DFW' },
            { to: '/blog', label: 'DFW Desi Guides' },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="inline-flex items-center gap-1.5 bg-white rounded-full border border-gray-200 px-4 py-2 text-[12px] font-semibold text-gray-600 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
